/* Campaigns, and the one sentence that has to be true of all of them: nothing is
 * being delivered.
 *
 * SENDING IS OFF FOR EVERY CHANNEL. So an active or a draft campaign has sent nothing
 * and SAYS SO IN WORDS. Four zeros and a dash read as a broken sync; "312 sent · 68%
 * open" beside "nothing is delivered" reads as a lie. Completed campaigns keep their
 * numbers, as history from before the switch, clamped at the audience by the source.
 *
 * THE AUDIENCE UNDER THE PICKER, BEFORE SAVE. It is the number segmentMembers returns,
 * which is the same list the dry run will render, and a suppressed member is never in
 * it (SITE-RULES 1).
 */
import { useState } from 'react';
import { useAsync, useSource } from '../data/SourceProvider.js';
import type { CampaignView, SegmentView } from '../data/contract.js';
import { EVERYONE } from '../data/SeedSource.js';
import { NoneRow } from '../components/Panel.js';
import { Field, Modal } from '../components/Modal.js';
import { useToast } from '../components/Toast.js';
import { fmt } from '../lib/fmt.js';

const STATUS_TONE: Record<string, string> = {
  active: 'good', draft: '', completed: 'info', scheduled: 'warn',
};

const TYPES = ['nurture', 'retention', 'winback', 'referral', 'announcement'] as const;
const CHANNELS = ['sms', 'email', 'email + sms'] as const;

const rate = (a: number, b: number): string => (b ? Math.round((a / b) * 100) + '%' : '—');

export function Campaigns() {
  const source = useSource();
  const [epoch, setEpoch] = useState(0);
  const [editing, setEditing] = useState<CampaignView | 'new' | null>(null);
  const [previewing, setPreviewing] = useState<CampaignView | null>(null);
  const { data, error } = useAsync(() => source.getCampaigns(), [source, epoch]);
  const segments = useAsync(() => source.getSegments(), [source]);

  return (
    <>
      <section className="panel">
        <header>
          <h2 className="t-heading-h3">Campaigns</h2>
          <span className="spacer" />
          <button type="button" className="btn primary t-label-small" onClick={() => setEditing('new')}>
            + New campaign
          </button>
        </header>
        <p className="t-body-small muted" style={{ marginBottom: 12 }}>
          Sending is off. Every campaign is rendered and logged, and nothing is delivered
          until Acrostic switches the channel on.
        </p>

        {error && <NoneRow>Could not read the campaigns. {error.message}</NoneRow>}
        {!error && !data && <NoneRow>Reading…</NoneRow>}
        {data && !data.length && <NoneRow>No campaigns yet.</NoneRow>}

        {data && data.length > 0 && (
          <table className="table">
            <thead>
              <tr className="t-label-small">
                <th>Campaign</th><th>Status</th><th className="right">Audience</th>
                <th className="right">Sent</th><th className="right">Open</th>
                <th className="right">Reply</th><th className="right">Converted</th><th />
              </tr>
            </thead>
            <tbody>
              {data.map((c) => {
                const quiet = !c.stats.sent && (c.status === 'active' || c.status === 'draft');
                return (
                  <tr key={c.id} onClick={() => setEditing(c)}>
                    <td>
                      <div>
                        <div className="t-label-large">{c.name}</div>
                        <div className="t-caption muted">{c.type} · {c.channel} · {c.segment}</div>
                      </div>
                    </td>
                    <td><span className={'badge ' + (STATUS_TONE[c.status] ?? '') + ' t-label-xsmall'}>{c.status}</span></td>
                    <td className="right t-body-small">{fmt.num(c.audience)}</td>
                    {quiet ? (
                      <td className="t-caption muted" colSpan={4}>Not yet sent</td>
                    ) : (
                      <>
                        <td className="right t-body-small">{fmt.num(c.stats.sent)}</td>
                        <td className="right t-body-small">{rate(c.stats.opened, c.stats.sent)}</td>
                        <td className="right t-body-small">{rate(c.stats.replied, c.stats.sent)}</td>
                        <td className="right t-label-medium">{c.stats.converted}</td>
                      </>
                    )}
                    <td className="right">
                      <button
                        type="button" className="btn t-label-small"
                        onClick={(e) => { e.stopPropagation(); setPreviewing(c); }}
                      >Preview</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {editing && (
        <Editor
          c={editing === 'new' ? null : editing}
          segments={segments.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setEpoch((n) => n + 1); }}
        />
      )}
      {previewing && <Preview c={previewing} onClose={() => setPreviewing(null)} />}
    </>
  );
}

function Editor({ c, segments, onClose, onSaved }: {
  c: CampaignView | null; segments: SegmentView[]; onClose: () => void; onSaved: () => void;
}) {
  const source = useSource();
  const toast = useToast();
  const [name, setName] = useState(c?.name ?? '');
  const [type, setType] = useState<CampaignView['type']>(c?.type ?? 'nurture');
  const [channel, setChannel] = useState<CampaignView['channel']>(c?.channel ?? 'sms');
  const [segmentId, setSegmentId] = useState(c?.segmentId ?? '');
  const [subject, setSubject] = useState(c?.subject ?? '');
  const [body, setBody] = useState(c?.body ?? '');
  const [busy, setBusy] = useState(false);

  /* The audience, live, under the picker. It is the segment's own count, so what is
     promised here is what the dry run will render. */
  const chosen = segments.find((s) => s.id === segmentId);
  const audience = useAsync(
    /* One row, for the total. The audience is a NUMBER here; asking for the people to
       count them is what the paging exists to stop. */
    () => segmentId ? source.getSegmentMembers(segmentId, { size: 1 }).then((x) => x.total)
      : source.getMembers({ status: 'active', size: 1 }).then((p) => p.total),
    [source, segmentId],
  );

  async function save() {
    if (!name.trim() || !body.trim()) { toast('A name and a message are required', 'bad'); return; }
    setBusy(true);
    try {
      await source.saveCampaign({
        ...(c ? { id: c.id } : {}),
        name: name.trim(), type, channel,
        segmentId: segmentId || null, subject, body,
      });
      toast('Campaign saved', 'good');
      onSaved();
    } catch (e) {
      toast((e as Error).message, 'bad');
      setBusy(false);
    }
  }

  async function remove() {
    if (!c) return;
    setBusy(true);
    await source.deleteCampaign(c.id);
    toast('Campaign deleted', 'good');
    onSaved();
  }

  return (
    <Modal
      title={c ? 'Edit · ' + c.name : 'New campaign'}
      onClose={onClose}
      footer={
        <>
          {c && <button type="button" className="btn t-label-medium" disabled={busy} onClick={() => void remove()}>Delete</button>}
          <span className="spacer" style={{ flex: 1 }} />
          <button type="button" className="btn t-label-medium" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary t-label-medium" disabled={busy} onClick={() => void save()}>
            Save campaign
          </button>
        </>
      }
    >
      <Field label="Name">
        <input className="field" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <div className="grid2">
        <Field label="Type">
          <select className="field" value={type} onChange={(e) => setType(e.target.value as CampaignView['type'])}>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Channel">
          <select className="field" value={channel} onChange={(e) => setChannel(e.target.value as CampaignView['channel'])}>
            {CHANNELS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>
      <Field
        label="Audience segment"
        hint={audience.data == null ? 'Counting…' : `${fmt.num(audience.data)} ${audience.data === 1 ? 'person' : 'people'}`}
      >
        <select className="field" value={segmentId} onChange={(e) => setSegmentId(e.target.value)}>
          <option value="">{EVERYONE}</option>
          {/* A review list is not an audience, so it is not in the picker. The source
              refuses it as well: the picker is a courtesy, the refusal is the rule. */}
          {segments.filter((s) => s.sendable).map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </Field>
      {chosen && <p className="t-caption muted" style={{ marginTop: -4 }}>{chosen.desc}</p>}
      <Field label="Subject" hint="Leave blank for SMS only.">
        <input className="field" value={subject} onChange={(e) => setSubject(e.target.value)} />
      </Field>
      <Field
        label="Message"
        hint="Merge fields: {{first_name}} {{gym_name}} {{goal}} {{coach}} {{class}} {{visits_30d}}"
      >
        <textarea className="field" rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
      </Field>
    </Modal>
  );
}

function Preview({ c, onClose }: { c: CampaignView; onClose: () => void }) {
  const source = useSource();
  const { data } = useAsync(() => source.dryRunCampaign(c.id), [source, c.id]);
  const sms = c.channel.includes('sms');
  /* Named against the CHANNEL, not against `sms`, which is also true of 'email + sms'
     and would have told an owner their members had no phone number when an email
     address would have reached them. */
  const missing = c.channel === 'sms' ? 'phone number'
    : c.channel === 'email' ? 'email address'
    : 'email address or phone number';
  return (
    <Modal
      title={'Preview · ' + c.name}
      onClose={onClose}
      footer={<button type="button" className="btn t-label-medium" onClick={onClose}>Done</button>}
    >
      {!data ? <NoneRow>Rendering…</NoneRow> : (
        <>
          {/* "Exactly what would go out" is only true of the people there is somewhere
              to send it to. On a real roster that is not all of them, and saying so
              here is the difference between a preview and a promise. */}
          <p className="notice warn t-body-small">
            Sending is disabled. {fmt.num(data.total)} messages were rendered and zero were
            sent. This is exactly what would go out once sending is switched on.
            {data.unreachable > 0 && (
              <> {fmt.num(data.unreachable)} of them{' '}
                {data.unreachable === 1 ? 'has' : 'have'} no {missing} on record, so{' '}
                {data.unreachable === 1 ? 'that one' : 'those'} would not be delivered.{' '}
                {fmt.num(data.total - data.unreachable)} would.</>
            )}
          </p>
          <div className="rows register">
            {data.preview.map((r) => (
              <div className="row" key={r.name}>
                <div className="who">
                  <div className="n t-label-large">{r.name}</div>
                  <div className="s t-caption muted">{(sms ? r.phone : r.email) ?? 'No contact detail on record'}</div>
                  <div className="s t-body-small" style={{ marginTop: 4 }}>{r.message}</div>
                </div>
              </div>
            ))}
          </div>
          {data.total > data.preview.length && (
            <p className="t-caption muted">Showing the first {data.preview.length} of {fmt.num(data.total)}.</p>
          )}
        </>
      )}
    </Modal>
  );
}
